// Split a free-form full-name string into { first_name, last_name } using
// the conventions members actually type or paste from a spreadsheet:
//
//   "Danny Godfrey"       → { first: "Danny", last: "Godfrey" }
//   "John George Doo"     → { first: "John George", last: "Doo" }  (last is final word)
//   "S Green"             → { first: "S", last: "Green" }
//   "Doo, John George"    → { first: "John George", last: "Doo" }  (comma-swap)
//   "Cher"                → { first: "", last: "Cher" }            (single-word)
//   "  Bill   Metson  "   → { first: "Bill", last: "Metson" }      (whitespace)
//
// Honorific suffixes (LRPS, ARPS, EFIAP, BPE3*, …) are STRIPPED from the
// last-name portion so importing "Bill Metson LRPS" doesn't fingerprint
// the record with a suffix that later renders on scoresheets. The suffix
// is returned in the third field so the caller can preserve it in the
// display_name if desired.
const HONORIFIC_SUFFIXES = new Set([
  "lrps", "arps", "frps", "efiap", "efiap/b", "efiap/s", "efiap/g", "efiap/p",
  "afiap", "mfiap", "cpagb", "dpagb", "mpagb", "apagb",
  "bpe", "abpe", "fbpe",
]);

const stripStar = (w) => w.replace(/\*+$/, "");

const isHonorificSuffix = (w) => {
  const s = stripStar(w).toLowerCase();
  return HONORIFIC_SUFFIXES.has(s) || /^bpe\d$/.test(s);
};

export function splitFullName(raw) {
  const s = (raw || "").replace(/\s+/g, " ").trim();
  if (!s) return { first_name: "", last_name: "", suffix: "" };

  // Comma form — "Doo, John" → last=Doo, first=John
  const commaIdx = s.indexOf(",");
  let head, tail;
  if (commaIdx > 0) {
    head = s.slice(0, commaIdx).trim();      // last name
    tail = s.slice(commaIdx + 1).trim();     // first names
    // Recombine as "first last …" then re-split via the space-form below
    // so honorific stripping still applies uniformly.
    return splitFullName(`${tail} ${head}`);
  }

  const words = s.split(" ").filter(Boolean);
  // Peel honorific suffixes off the end
  const suffixes = [];
  while (words.length > 1 && isHonorificSuffix(words[words.length - 1])) {
    suffixes.unshift(words.pop());
  }

  if (words.length === 0) return { first_name: "", last_name: s, suffix: "" };
  if (words.length === 1) return { first_name: "", last_name: words[0], suffix: suffixes.join(" ") };

  const last_name = words.pop();
  const first_name = words.join(" ");
  return { first_name, last_name, suffix: suffixes.join(" ") };
}

// Reverse: rebuild a full-name display string. Suffix rejoined with a
// space so "Bill Metson LRPS" round-trips.
export function joinFullName({ first_name = "", last_name = "", suffix = "" }) {
  return [first_name, last_name, suffix].map((s) => (s || "").trim()).filter(Boolean).join(" ");
}
